import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  LayoutDashboard,
  FolderKanban,
  FileText,
  FileSignature,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  User,
  Users,
  UserCheck,
  UserPlus,
  Building2,
  DollarSign,
  Search,
  Eye,
  AlertCircle,
  MessageSquare,
  Check,
  ShoppingBag,
  Wrench,
  UserCog,
  Star,
  Palette,
  ArrowLeftRight,
  Shield,
  Home,
  ChevronLeft,
  PanelLeftClose,
  PanelLeft,
  TrendingUp,
  Globe,
  Award,
  Database,
  BarChart3,
  Megaphone,
  Store,
  Wallet,
  ChevronDown,
  ClipboardCheck,
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useTranslation } from "react-i18next";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DashboardLayout = ({ children, navItems, title }) => {
  const { user, logout, setUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try {
      const saved = localStorage.getItem("nav_collapsed_sections");
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const toggleSection = (sectionId) => {
    setCollapsedSections((prev) => {
      const next = { ...prev, [sectionId]: !prev[sectionId] };
      try {
        localStorage.setItem("nav_collapsed_sections", JSON.stringify(next));
      } catch {}
      return next;
    });
  };

  // Translation mapping for nav labels
  const translateLabel = (label) => {
    const labelMap = {
      'Dashboard': t('nav.dashboard'),
      'Local Projects': t('nav.localProjects'),
      'Browse Projects': t('projects.searchProjects'),
      'Project Approval': t('nav.projectApproval'),
      'All Projects': t('projects.allProjects'),
      'Applications': t('nav.applications'),
      'Promotions': t('nav.promotions'),
      'Workers': t('nav.workers'),
      'Project Contracts': t('nav.contracts'),
      'Employment Contracts': t('nav.employmentContracts'),
      'Role Management': t('roles.manageRoles'),
      'Webshop': t('nav.webshop'),
      'Shop Appearance': t('webshop.title'),
      'Rentals': t('nav.rentals'),
      'Earnings & Payroll': t('nav.earnings'),
      'Invoices': t('nav.invoices'),
      'My Projects': t('projects.myProjects'),
      'My Applications': t('applications.myApplications'),
      'Agreements': t('nav.agreements'),
      'My Contract': t('contracts.employmentContract'),
      'Assignments': t('nav.assignments'),
      'My Earnings': t('nav.earnings'),
      'Timesheets': t('nav.timesheets'),
      'Profile': t('nav.profile'),
      'Shop': t('webshop.title'),
      'Rent Tools': t('rentals.title'),
      'Public Shop': t('webshop.title'),
      'Public Rentals': t('rentals.title'),
      'Find Colleague': t('workers.hireWorker'),
      'My Teams': t('nav.teams'),
      'Contracts': t('nav.contracts'),
      'Products & Orders': t('webshop.products'),
      'Appearance': t('webshop.title'),
      'View Public Shop': t('webshop.title'),
      'Equipment & Requests': t('rentals.equipment'),
      'View Public Rentals': t('rentals.title'),
      'Home': t('nav.home'),
      'Hjem': t('nav.home'),
      'Login As User': t('common.search'),
      'View As Role': t('auth.role'),
      // Client menu items
      'Publiser Prosjekt': t('nav.requestQuote'),
      'Post Project': t('nav.requestQuote'),
      'Stillastjenester': t('nav.scaffoldingServices'),
      'Scaffolding Services': t('nav.scaffoldingServices'),
      'Mine Prosjekter': t('projects.myProjects'),
      'My Projects': t('projects.myProjects'),
      'Kontrakter': t('contracts.title'),
      'Contracts': t('contracts.title'),
      'Fakturaer': t('invoices.title'),
      'Invoices': t('invoices.title'),
      'Butikk': t('nav.shop'),
      'Shop': t('nav.shop'),
      'Lei Utstyr': t('nav.rentTools'),
      'Rent Tools': t('nav.rentTools'),
      // Holding menu items
      'Dashbord': t('nav.dashboard'),
      'Dashboard': t('nav.dashboard'),
      'Prosjekter': t('projects.title'),
      'Projects': t('projects.title'),
      'Browse Projects': t('nav.browseProjects'),
      'Project Approval': t('nav.projectApproval'),
      'All Projects': t('projects.allProjects'),
      'Stillastjenester Admin': t('nav.scaffoldingAdmin'),
      // Worker menu items
      'My Applications': t('nav.myApplications'),
      'Agreements': t('nav.agreements'),
      'My Contract': t('nav.myContract'),
      'Assignments': t('nav.assignments'),
      'My Earnings': t('nav.myEarnings'),
      'Timesheets': t('nav.timesheets'),
      'Profile': t('nav.profile'),
      // Team Leader
      'Teams': t('nav.teams'),
      'Workers': t('nav.workers'),
      // Common
      'Logg ut': t('nav.logout'),
      'Log out': t('nav.logout'),
      '⭐ Become Team Leader': t('nav.becomeTeamLeader'),
      'Become Team Leader': t('nav.becomeTeamLeader'),
      '⭐ Bli Team Leader': t('nav.becomeTeamLeader'),
      // Norwegian worker/TL labels
      'Ledige Prosjekter': t('nav.browseProjects'),
      'Mine Prosjekter': t('projects.myProjects'),
      'Mine Søknader': t('nav.myApplications'),
      'Avtaler': t('nav.agreements'),
      'Min Kontrakt': t('nav.myContract'),
      'Oppdrag': t('nav.assignments'),
      'Mine Inntekter': t('nav.myEarnings'),
      'Min Inntekt': t('nav.myEarnings'),
      'Timelister': t('nav.timesheets'),
      'Profil': t('nav.profile'),
      'Finn Kollega': t('workers.hireWorker'),
      'Mine Teams': t('nav.teams'),
      'Lei Verktøy': t('nav.rentTools'),
      'Lei Utstyr': t('nav.rentEquipment'),
      'Rent Equipment': t('nav.rentEquipment'),
      'Find Colleague': t('workers.hireWorker'),
      'My Teams': t('nav.teams')
    };
    return labelMap[label] || label;
  };
  // Persist viewAsRole in localStorage for refresh stability
  const [viewAsRole, setViewAsRole] = useState(() => {
    const saved = localStorage.getItem('admin_view_as_role');
    return saved && saved !== 'null' ? saved : null;
  });
  const [pendingAgreements, setPendingAgreements] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const notificationRef = useRef(null);
  
  // Pending project approvals for admin
  const [pendingProjectApprovals, setPendingProjectApprovals] = useState(0);
  
  // Client projects needing attention (new applications, etc.)
  const [clientProjectsNeedingAttention, setClientProjectsNeedingAttention] = useState(0);
  
  // Team Leader projects needing attention (contracts to sign, etc.)
  const [tlProjectsNeedingAttention, setTlProjectsNeedingAttention] = useState(0);
  
  // Worker projects needing attention (contracts to sign)
  const [workerProjectsNeedingAttention, setWorkerProjectsNeedingAttention] = useState(0);
  
  // Holding Admin projects needing attention (pending reviews, counter-offers)
  const [holdingProjectsNeedingAttention, setHoldingProjectsNeedingAttention] = useState(0);
  
  // Impersonation state
  const [impersonationMode, setImpersonationMode] = useState(false);
  const [impersonatedBy, setImpersonatedBy] = useState(null);
  const [testAccounts, setTestAccounts] = useState([]);
  const [impersonateDialogOpen, setImpersonateDialogOpen] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  
  // Quick impersonate by code
  const [quickImpersonateCode, setQuickImpersonateCode] = useState("");
  const [searchingCode, setSearchingCode] = useState(false);
  
  // Admin "View As" functionality - allows admin to see other dashboards
  const isAdmin = user?.role === "platform_admin" || user?.role === "operations_manager";
  
  // Track active discounts for Shop badge
  const [activeDiscountCount, setActiveDiscountCount] = useState(0);
  const [maxDiscountPercent, setMaxDiscountPercent] = useState(0);
  
  // Track notifications by category/tab for red dot indicators
  const [notificationsByTab, setNotificationsByTab] = useState({
    projects: 0,      // My Projects, Browse Projects
    applications: 0,  // My Applications
    contracts: 0,     // Contracts
    messages: 0,      // Messages/Chat
    dashboard: 0      // Dashboard
  });
  
  // Fetch pending agreements count for badge
  useEffect(() => {
    const fetchCounts = async () => {
      if (!user) return;
      
      try {
        // Get pending agreements for workers/TLs
        if (user.role === "worker" || user.role === "team_leader") {
          const res = await axios.get(`${API}/team-agreements`);
          // Handle both array response and {agreements: [...]} response
          const agreementsData = Array.isArray(res.data) ? res.data : (res.data?.agreements || []);
          const pending = agreementsData.filter(a => 
            a.status === "pending_approval" || 
            a.members?.some(m => m.user_id === user.user_id && !m.signed)
          );
          setPendingAgreements(pending.length);
        }
        
        // Get unread notifications
        const notifRes = await axios.get(`${API}/notifications/unread-count`).catch(() => ({ data: { count: 0 } }));
        setUnreadNotifications(notifRes.data?.count || 0);
        
        // Get notifications to categorize by tab
        const allNotifsRes = await axios.get(`${API}/notifications`).catch(() => ({ data: [] }));
        const unreadNotifs = Array.isArray(allNotifsRes.data) 
          ? allNotifsRes.data.filter(n => !n.read) 
          : [];
        
        // Categorize notifications by tab
        const tabCounts = {
          projects: 0,
          applications: 0,
          contracts: 0,
          messages: 0,
          dashboard: 0
        };
        
        unreadNotifs.forEach(notif => {
          const type = notif.type || '';
          const link = notif.link || '';
          
          // Check notification type and link to determine category
          if (type.includes('project') || type === 'counter_offer' || type === 'new_project' || 
              link.includes('/projects') || link.includes('/project')) {
            tabCounts.projects++;
          } else if (type.includes('application') || link.includes('/applications')) {
            tabCounts.applications++;
          } else if (type.includes('contract') || link.includes('/contract')) {
            tabCounts.contracts++;
          } else if (type.includes('message') || type === 'negotiation_message' || type === 'chat_message') {
            tabCounts.projects++; // Chat messages go to projects tab (contact tab is within project)
          } else {
            tabCounts.dashboard++;
          }
        });
        
        setNotificationsByTab(tabCounts);
        
        // Get active discounts from shop
        const productsRes = await axios.get(`${API}/shop/products`).catch(() => ({ data: [] }));
        const now = new Date();
        const discountedProducts = productsRes.data.filter(p => {
          if (!p.discount_enabled || !p.discount_percent) return false;
          if (p.discount_permanent) return true;
          if (p.discount_start_date && new Date(p.discount_start_date) > now) return false;
          if (p.discount_end_date && new Date(p.discount_end_date) < now) return false;
          return true;
        });
        setActiveDiscountCount(discountedProducts.length);
        if (discountedProducts.length > 0) {
          const maxDiscount = Math.max(...discountedProducts.map(p => p.discount_percent || 0));
          setMaxDiscountPercent(maxDiscount);
        }
        
        // Get pending project approvals count for admin
        if (user.role === "platform_admin" || user.role === "operations_manager") {
          const pendingRes = await axios.get(`${API}/admin/projects/pending-count`).catch(() => ({ data: { count: 0 } }));
          setPendingProjectApprovals(pendingRes.data?.count || 0);
        }
        
        // Get projects needing attention for client
        if (user.role === "client_admin" || user.role === "client") {
          const clientRes = await axios.get(`${API}/client/projects/attention-count`).catch(() => ({ data: { count: 0 } }));
          setClientProjectsNeedingAttention(clientRes.data?.count || 0);
        }
        
        // Get projects needing attention for team leader (unsigned contracts, etc.)
        if (user.role === "team_leader") {
          const tlRes = await axios.get(`${API}/team/projects/attention-count`).catch(() => ({ data: { count: 0 } }));
          setTlProjectsNeedingAttention(tlRes.data?.count || 0);
        }
        
        // Get projects needing attention for worker (unsigned contracts)
        if (user.role === "worker") {
          const workerRes = await axios.get(`${API}/worker/projects/attention-count`).catch(() => ({ data: { count: 0 } }));
          setWorkerProjectsNeedingAttention(workerRes.data?.count || 0);
        }
        
        // Get projects needing attention for holding admin (pending reviews, counter-offers)
        if (user.role === "holding_admin") {
          const holdingRes = await axios.get(`${API}/projects-v3/holding/attention-count`).catch(() => ({ data: { count: 0 } }));
          setHoldingProjectsNeedingAttention(holdingRes.data?.count || 0);
        }
      } catch (err) {
        console.error("Error fetching counts:", err);
      }
    };
    
    fetchCounts();
    // Refresh every 30 seconds
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, [user]);

  // Close notification dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if we're in impersonation mode (check session)
  useEffect(() => {
    const checkImpersonation = async () => {
      try {
        // Check if current session has impersonation flag
        const savedImpersonation = localStorage.getItem('impersonation_info');
        if (savedImpersonation) {
          const info = JSON.parse(savedImpersonation);
          setImpersonationMode(true);
          setImpersonatedBy(info);
        }
      } catch (err) {
        console.error("Error checking impersonation:", err);
      }
    };
    checkImpersonation();
  }, []);

  // Fetch test accounts for quick impersonation
  useEffect(() => {
    const fetchTestAccounts = async () => {
      if (!isAdmin || impersonationMode) return;
      try {
        const res = await axios.get(`${API}/admin/test-accounts`);
        setTestAccounts(res.data);
      } catch (err) {
        console.error("Error fetching test accounts:", err);
      }
    };
    fetchTestAccounts();
  }, [isAdmin, impersonationMode]);

  // Quick impersonate by worker code (TL-001, WRK-003, etc.)
  const quickImpersonateByCode = async () => {
    if (!quickImpersonateCode.trim()) return;
    
    setSearchingCode(true);
    try {
      // Search for user by worker code
      const searchRes = await axios.get(`${API}/admin/users/search`, {
        params: { worker_code: quickImpersonateCode.trim().toUpperCase() }
      });
      
      if (searchRes.data && searchRes.data.user_id) {
        // Found user, now impersonate
        await impersonateUser(searchRes.data.user_id);
        setQuickImpersonateCode("");
      } else {
        toast.error(`No user found with code: ${quickImpersonateCode}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || `User not found: ${quickImpersonateCode}`);
    } finally {
      setSearchingCode(false);
    }
  };

  // Impersonate user function
  const impersonateUser = async (targetUserId) => {
    setImpersonating(true);
    try {
      const res = await axios.post(`${API}/admin/impersonate/${targetUserId}`);
      
      // Save impersonation info to localStorage
      localStorage.setItem('impersonation_info', JSON.stringify(res.data.impersonated_by));
      localStorage.setItem('scaffoldx_token', res.data.token);  // Use correct token key
      
      // Update user in context
      if (setUser) {
        setUser(res.data.user);
      }
      
      setImpersonationMode(true);
      setImpersonatedBy(res.data.impersonated_by);
      setImpersonateDialogOpen(false);
      
      toast.success(`Now viewing as ${res.data.user.name} (${res.data.user.role})`);
      
      // Navigate to appropriate dashboard
      const roleRoutes = {
        platform_admin: '/admin',
        operations_manager: '/admin',
        client_admin: '/client',
        client_viewer: '/client',
        team_leader: '/team-leader',
        worker: '/worker',
        webshop_admin: '/admin/webshop',
        rentals_admin: '/admin/rentals'
      };
      navigate(roleRoutes[res.data.user.role] || '/');
      
      // Reload to apply new session
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to impersonate user");
    } finally {
      setImpersonating(false);
    }
  };

  // Stop impersonation function
  const stopImpersonation = async () => {
    try {
      const res = await axios.post(`${API}/admin/stop-impersonation`);
      
      // Clear impersonation info
      localStorage.removeItem('impersonation_info');
      localStorage.setItem('scaffoldx_token', res.data.token);  // Use correct token key
      
      // Update user in context
      if (setUser) {
        setUser(res.data.user);
      }
      
      setImpersonationMode(false);
      setImpersonatedBy(null);
      
      toast.success("Returned to admin account");
      navigate('/admin');
      
      // Reload to apply new session
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to stop impersonation");
    }
  };

  // Fetch notifications
  const fetchNotifications = async () => {
    if (!user) return;
    setLoadingNotifications(true);
    try {
      const res = await axios.get(`${API}/notifications`);
      setNotifications(res.data || []);
    } catch (err) {
      console.error("Error fetching notifications:", err);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const toggleNotifications = () => {
    if (!showNotifications) {
      fetchNotifications();
    }
    setShowNotifications(!showNotifications);
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      setNotifications(prev => prev.map(n => 
        n.notification_id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadNotifications(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/mark-all-read`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadNotifications(0);
    } catch (err) {
      console.error("Error marking all as read:", err);
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      await markAsRead(notification.notification_id);
    }
    if (notification.link) {
      navigate(notification.link);
      setShowNotifications(false);
    }
  };
  
  // Get the appropriate nav items based on viewAsRole
  const getNavItemsForRole = (role) => {
    switch(role) {
      case "worker": return workerNavItems;
      case "team_leader": return teamLeaderNavItems;
      case "client_admin": return clientNavItems;
      case "client_private": return clientPrivateNavItems;
      case "webshop_admin": return webshopAdminNavItems;
      case "rentals_admin": return rentalsAdminNavItems;
      case "operations_manager": return operationsManagerNavItems;
      case "holding_admin": return holdingAdminNavItems;
      case "platform_admin": return adminNavItems;
      default: return adminNavItems;
    }
  };
  
  // Use viewAsRole if set, otherwise use user's actual role
  const effectiveRole = viewAsRole || user?.role;
  const activeNavItems = effectiveRole ? getNavItemsForRole(effectiveRole) : (navItems || adminNavItems);
  const displayTitle = viewAsRole ? `${title} (Viewing as ${viewAsRole.replace(/_/g, " ")})` : title;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };
  
  const handleViewAsChange = (role) => {
    if (role === "admin") {
      setViewAsRole(null);
      localStorage.removeItem('admin_view_as_role');
      navigate("/admin");
    } else {
      setViewAsRole(role);
      localStorage.setItem('admin_view_as_role', role);
      // Navigate to the corresponding dashboard
      const routes = {
        holding_admin: "/holding",
        worker: "/worker",
        team_leader: "/team-leader",
        client_admin: "/client",
        client_private: "/client",
        webshop_admin: "/admin/webshop",
        rentals_admin: "/admin/rentals",
        operations_manager: "/admin"
      };
      navigate(routes[role] || "/admin");
    }
  };
  
  // Clear viewAsRole when user logs out or changes
  useEffect(() => {
    if (!user || !isAdmin) {
      setViewAsRole(null);
      localStorage.removeItem('admin_view_as_role');
    }
  }, [user, isAdmin]);
  
  // Check if item should show badge
  const getBadgeCount = (label) => {
    if (label === "Agreements" && pendingAgreements > 0) return pendingAgreements;
    if (label === "My Applications" && pendingAgreements > 0) return pendingAgreements;
    if (label === "Project Approval" && pendingProjectApprovals > 0) return pendingProjectApprovals;
    
    // For client - show on My Projects (combine attention + notifications)
    if ((label === "My Projects" || label === "Mine Prosjekter") && user?.role?.includes("client")) {
      const count = clientProjectsNeedingAttention + notificationsByTab.projects;
      return count > 0 ? count : 0;
    }
    // For team leader - show on My Projects
    if ((label === "My Projects" || label === "Mine prosjekter") && user?.role === "team_leader") {
      const count = tlProjectsNeedingAttention + notificationsByTab.projects;
      return count > 0 ? count : 0;
    }
    // For worker - show on My Projects
    if ((label === "My Projects" || label === "Mine prosjekter") && user?.role === "worker") {
      const count = workerProjectsNeedingAttention + notificationsByTab.projects;
      return count > 0 ? count : 0;
    }
    
    // Holding Admin - Prosjekter tab (Norwegian label)
    if ((label === "Prosjekter" || label === "Projects") && user?.role === "holding_admin") {
      const count = holdingProjectsNeedingAttention + notificationsByTab.projects;
      return count > 0 ? count : 0;
    }
    
    // Holding Admin - legacy label support
    if ((label === "Browse Projects" || label === "All Projects" || label === "Project Approval") && 
        (user?.role === "holding_admin" || user?.role === "platform_admin")) {
      return notificationsByTab.projects > 0 ? notificationsByTab.projects : 0;
    }
    
    // Applications tab for any role
    if (label === "Applications" || label === "My Applications") {
      return notificationsByTab.applications > 0 ? notificationsByTab.applications : 0;
    }
    
    // Contracts tab (EN and NO)
    if (label === "Contracts" || label === "Project Contracts" || label === "My Contract" || 
        label === "Kontrakter" || label === "Min Kontrakt") {
      return notificationsByTab.contracts > 0 ? notificationsByTab.contracts : 0;
    }
    
    // Dashboard (EN and NO)
    if ((label === "Dashboard" || label === "Dashbord") && notificationsByTab.dashboard > 0) {
      return notificationsByTab.dashboard;
    }
    
    return 0;
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Impersonation Banner */}
      {impersonationMode && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2 px-4 flex items-center justify-center gap-4 shadow-lg pt-[env(safe-area-inset-top,0px)]">
          <UserCog className="w-5 h-5" />
          <span className="font-medium">
            Viewing as <strong>{user?.name}</strong> ({user?.role?.replace(/_/g, ' ')})
          </span>
          <span className="text-white/70">|</span>
          <span className="text-sm text-white/80">
            Impersonated by {impersonatedBy?.name}
          </span>
          <Button
            onClick={stopImpersonation}
            size="sm"
            className="bg-white text-purple-600 hover:bg-white/90 ml-4"
          >
            <ArrowLeftRight className="w-4 h-4 mr-2" />
            Return to Admin
          </Button>
        </div>
      )}

      {/* Mobile Header */}
      <div className={`md:hidden fixed ${impersonationMode ? 'top-10' : 'top-[env(safe-area-inset-top,0px)]'} left-0 right-0 z-50 bg-[#0A0A0A] border-b border-white/10 px-4 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-3">
          {/* Enhanced Menu Button - More visible */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="relative flex items-center gap-2 px-3 py-2 bg-[#23F7DD]/10 border border-[#23F7DD]/30 text-[#23F7DD] hover:bg-[#23F7DD]/20 rounded-lg transition-all"
            data-testid="mobile-menu-toggle"
          >
            <PanelLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Meniu</span>
            {/* Show notification badge on menu button when closed */}
            {unreadNotifications > 0 && (
              <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-bold animate-pulse">
                {unreadNotifications > 9 ? '9+' : unreadNotifications}
              </span>
            )}
          </button>
        </div>
        <div className="flex items-center gap-2 relative" ref={notificationRef}>
          <LanguageSwitcher />
          <button 
            onClick={toggleNotifications}
            className="p-2 text-[#94A3B8] hover:text-white relative"
            data-testid="notification-bell"
          >
            <Bell className="w-5 h-5" />
            {unreadNotifications > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-bold animate-pulse">
                {unreadNotifications > 9 ? '9+' : unreadNotifications}
              </span>
            )}
          </button>
          
          {/* Notification Dropdown - Mobile */}
          {showNotifications && (
            <div className="absolute top-12 right-0 w-80 max-h-96 bg-[#0A0A0A] border border-white/10 rounded-lg shadow-xl overflow-hidden z-50">
              <div className="p-3 border-b border-white/10 flex items-center justify-between">
                <span className="font-semibold text-white">Notifications</span>
                {unreadNotifications > 0 && (
                  <button 
                    onClick={markAllAsRead}
                    className="text-xs text-[#23F7DD] hover:underline"
                  >
                    Mark all read
                  </button>
                )}
              </div>
              <div className="max-h-72 overflow-y-auto">
                {loadingNotifications ? (
                  <div className="p-4 text-center">
                    <div className="w-6 h-6 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin mx-auto"></div>
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="p-6 text-center text-[#94A3B8]">
                    <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No notifications</p>
                  </div>
                ) : (
                  notifications.slice(0, 10).map((notif) => (
                    <div
                      key={notif.notification_id}
                      onClick={() => handleNotificationClick(notif)}
                      className={`p-3 border-b border-white/5 cursor-pointer hover:bg-white/5 transition-colors ${!notif.read ? 'bg-[#23F7DD]/5' : ''}`}
                    >
                      <div className="flex items-start gap-2">
                        {!notif.read && <div className="w-2 h-2 rounded-full bg-[#23F7DD] mt-1.5 flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${!notif.read ? 'text-white' : 'text-[#94A3B8]'}`}>
                            {notif.message}
                          </p>
                          <p className="text-xs text-[#475569] mt-1">
                            {new Date(notif.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Full-Screen Menu */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-[60] bg-[#050505] overflow-y-auto">
          {/* Header with close button */}
          <div className="sticky top-0 bg-[#050505] border-b border-white/10 px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-1" onClick={() => setSidebarOpen(false)}>
              <div className="px-2 py-1 bg-[#0A0A0A] border border-[#23F7DD]/50 rounded flex items-center justify-center">
                <span className="text-[#23F7DD] font-bold text-base font-['Unbounded']">All</span>
              </div>
              <span className="text-lg font-bold font-['Unbounded'] neon-text-subtle">Stillas</span>
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-red-500/20 text-white hover:text-red-400 rounded-lg transition-all"
              data-testid="close-mobile-menu"
            >
              <X className="w-5 h-5" />
              <span className="text-sm font-medium">Lukk</span>
            </button>
          </div>
          
          <div className="p-4 space-y-4">
            {/* User Info Card */}
            <div className="p-4 bg-white/5 rounded-xl border border-white/10">
              <div className="flex items-center gap-4">
                {user?.picture ? (
                  <img
                    src={user.picture.startsWith('http') 
                      ? user.picture 
                      : `${process.env.REACT_APP_BACKEND_URL}${user.picture}`}
                    alt={user.name}
                    className="w-14 h-14 rounded-full object-cover border-2 border-[#23F7DD]/30"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-[#23F7DD]/20 flex items-center justify-center border-2 border-[#23F7DD]/30">
                    <User className="w-7 h-7 text-[#23F7DD]" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-lg truncate">{user?.name}</p>
                  <p className="text-sm text-[#94A3B8] truncate">{user?.email}</p>
                  <p className="text-xs text-[#23F7DD] mt-1">
                    {user?.role_display || user?.role?.replace(/_/g, " ")}
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Links - Shop & Rentals */}
            <div className="grid grid-cols-2 gap-3">
              <Link
                to="/webshop"
                onClick={() => setSidebarOpen(false)}
                className="flex flex-col items-center gap-2 p-4 rounded-xl bg-gradient-to-br from-[#F7931A]/20 to-[#FFD700]/10 border border-[#F7931A]/30 hover:border-[#F7931A] transition-all"
              >
                <ShoppingBag className="w-8 h-8 text-[#F7931A]" />
                <span className="font-medium text-[#F7931A] text-sm">{t('webshop.title')}</span>
                {activeDiscountCount > 0 && (
                  <span className="px-2 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full">
                    -{maxDiscountPercent}%
                  </span>
                )}
              </Link>
              <Link
                to="/rentals"
                onClick={() => setSidebarOpen(false)}
                className="flex flex-col items-center gap-2 p-4 rounded-xl bg-gradient-to-br from-[#F7931A]/20 to-[#FFD700]/10 border border-[#F7931A]/30 hover:border-[#F7931A] transition-all"
              >
                <Wrench className="w-8 h-8 text-[#F7931A]" />
                <span className="font-medium text-[#F7931A] text-sm">{t('rentals.title')}</span>
              </Link>
            </div>

            {/* Main Navigation - Large Touch Targets */}
            <div className="space-y-2">
              <p className="text-xs text-[#64748B] uppercase tracking-wider px-2 mb-3">Navigasjon</p>
              {activeNavItems.filter(item => !item.external && item.type !== "divider").map((item, sectionIdx) => {
                const renderLink = (it) => {
                  if (it.promotionLink && user?.role === 'team_leader') return null;
                  const isActive = location.pathname === it.href;
                  const badgeCount = getBadgeCount(it.label);
                  return (
                    <Link
                      key={it.href}
                      to={it.href}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center justify-between p-4 rounded-xl transition-all ${
                        isActive
                          ? "bg-[#23F7DD]/15 border-2 border-[#23F7DD]/50"
                          : "bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20"
                      }`}
                      data-testid={`mobile-nav-${it.label.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${isActive ? 'bg-[#23F7DD]/20' : 'bg-white/10'}`}>
                          {it.icon}
                        </div>
                        <span className={`font-medium text-base ${isActive ? 'text-[#23F7DD]' : 'text-white'}`}>
                          {translateLabel(it.label)}
                        </span>
                      </div>
                      {badgeCount > 0 && (
                        <span className="flex items-center justify-center min-w-[24px] h-6 px-2 text-sm font-bold bg-[#FF5F1F] text-white rounded-full">
                          {badgeCount}
                        </span>
                      )}
                    </Link>
                  );
                };

                if (item.type === "section") {
                  return (
                    <div key={`m-sec-${item.id}`} className="space-y-2">
                      <div className="pt-3 pb-1 px-2 text-xs font-semibold uppercase tracking-wider text-[#64748B] flex items-center gap-2">
                        {item.icon}
                        {item.label}
                      </div>
                      {item.children.map(renderLink)}
                    </div>
                  );
                }

                return renderLink(item);
              })}
            </div>

            {/* Language Switcher */}
            <div className="pt-4 border-t border-white/10">
              <LanguageSwitcher variant="full" className="w-full justify-center" />
            </div>

            {/* Logout Button */}
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full py-6 text-[#94A3B8] hover:text-[#FF5F1F] hover:bg-[#FF5F1F]/10 border border-white/10 rounded-xl"
              data-testid="mobile-logout-btn"
            >
              <LogOut className="w-5 h-5 mr-3" />
              {t('auth.logout')}
            </Button>

            {/* Version & Powered by */}
            <div className="text-center pb-8 space-y-2">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-[#23F7DD]/10 to-transparent border border-[#23F7DD]/20 rounded-full">
                <span className="text-[#23F7DD] text-[10px]">⚡</span>
                <span className="text-[10px] text-[#94A3B8]">Powered by</span>
                <span className="text-[10px] text-[#23F7DD] font-medium">Doserne AS</span>
              </div>
              <div>
                <span className="text-xs text-[#475569]">v{process.env.REACT_APP_VERSION}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Sidebar - Hidden on mobile */}
      <aside
        className={`hidden md:flex fixed top-0 left-0 h-full w-72 bg-[#0A0A0A] border-r border-white/10 z-50 flex-col overflow-hidden`}
      >
        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Header with Logo */}
          <div className="flex items-center justify-between mb-4">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-1">
              <div className="px-2 py-1 bg-[#0A0A0A] border border-[#23F7DD]/50 rounded flex items-center justify-center">
                <span className="text-[#23F7DD] font-bold text-base font-['Unbounded']">All</span>
              </div>
              <span className="text-lg font-bold font-['Unbounded'] neon-text-subtle">Stillas</span>
            </Link>
          </div>
          
          {/* Powered by Doserne Badge */}
          <div className="mb-6 pl-1">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-[#23F7DD]/10 to-transparent border border-[#23F7DD]/20 rounded-full">
              <span className="text-[#23F7DD] text-[10px]">⚡</span>
              <span className="text-[10px] text-[#94A3B8]">Powered by</span>
              <span className="text-[10px] text-[#23F7DD] font-medium">Doserne AS</span>
            </div>
          </div>

          {/* Home Page Link */}
          <Link 
            to="/" 
            className="flex items-center gap-3 p-3 mb-4 bg-gradient-to-r from-[#23F7DD]/10 to-transparent border border-[#23F7DD]/20 rounded-lg text-[#23F7DD] hover:bg-[#23F7DD]/20 transition-all"
            data-testid="nav-home-link"
          >
            <Home className="w-5 h-5" />
            <span className="font-medium">{t('nav.home')}</span>
          </Link>

          {/* User Info */}
          <div className="p-4 bg-white/5 rounded-lg mb-6">
            <div className="flex items-center gap-3">
              {user?.picture ? (
                <img
                  src={user.picture.startsWith('http') 
                    ? user.picture 
                    : `${process.env.REACT_APP_BACKEND_URL}${user.picture}`}
                  alt={user.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#23F7DD]/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-[#23F7DD]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{user?.name}</p>
                {user?.email && (
                  <p className="text-xs text-[#94A3B8] truncate" title={user.email}>
                    {user.email}
                  </p>
                )}
                {user?.company_name && (
                  <p className="text-xs text-[#23F7DD] truncate">
                    {user.company_name}
                  </p>
                )}
                <p className="text-xs text-[#94A3B8]">
                  {user?.role_display || user?.role?.replace(/_/g, " ")}
                </p>
                {user?.worker_code && (
                  <p className="text-xs text-[#64748B] font-mono mt-0.5">
                    {user.worker_code}
                  </p>
                )}
              </div>
            </div>
          </div>
          
          {/* Admin "View As" Selector */}
          {isAdmin && (
            <div className="mb-6 p-3 bg-[#4F46E5]/10 border border-[#4F46E5]/30 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Eye className="w-4 h-4 text-[#4F46E5]" />
                <span className="text-sm text-[#4F46E5] font-medium">{t('auth.role')}</span>
              </div>
              <Select value={viewAsRole || "admin"} onValueChange={handleViewAsChange}>
                <SelectTrigger className="bg-black/50 border-white/10 text-white focus:border-[#4F46E5] h-9" data-testid="view-as-select">
                  <SelectValue placeholder={t('auth.selectRole')} />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="admin" className="text-white hover:bg-white/10">
                    🔑 {t('roles.platformAdmin')}
                  </SelectItem>
                  <SelectItem value="holding_admin" className="text-white hover:bg-white/10">
                    🏛️ {t('roles.holdingAdmin')}
                  </SelectItem>
                  <SelectItem value="operations_manager" className="text-white hover:bg-white/10">
                    📋 {t('roles.operationsManager')}
                  </SelectItem>
                  <SelectItem value="webshop_admin" className="text-white hover:bg-white/10">
                    🛒 {t('nav.webshop')}
                  </SelectItem>
                  <SelectItem value="rentals_admin" className="text-white hover:bg-white/10">
                    🔧 {t('nav.rentals')}
                  </SelectItem>
                  <SelectItem value="team_leader" className="text-white hover:bg-white/10">
                    👷 {t('roles.teamLeader')}
                  </SelectItem>
                  <SelectItem value="worker" className="text-white hover:bg-white/10">
                    🧑‍🔧 {t('roles.worker')}
                  </SelectItem>
                  <SelectItem value="client_admin" className="text-white hover:bg-white/10">
                    🏢 {t('roles.clientAdmin')}
                  </SelectItem>
                  <SelectItem value="client_private" className="text-white hover:bg-white/10">
                    👤 {t('roles.clientViewer')}
                  </SelectItem>
                </SelectContent>
              </Select>
              {viewAsRole && (
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-[#94A3B8]">
                    {viewAsRole.replace(/_/g, " ")}
                  </p>
                  <button 
                    onClick={() => handleViewAsChange("admin")}
                    className="text-xs text-[#4F46E5] hover:text-[#6366F1] font-medium"
                    data-testid="reset-view-as"
                  >
                    {t('common.reset')}
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Quick Impersonate by Code */}
          {isAdmin && !impersonationMode && (
            <div className="mb-6 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <UserCog className="w-4 h-4 text-purple-400" />
                <span className="text-sm text-purple-400 font-medium">{t('common.search')}</span>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={quickImpersonateCode}
                  onChange={(e) => setQuickImpersonateCode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") quickImpersonateByCode();
                  }}
                  placeholder="TL-001, WRK-003..."
                  className="flex-1 bg-black/50 border border-white/20 text-white px-3 py-1.5 rounded text-sm placeholder:text-white/30 focus:border-purple-500 focus:outline-none"
                  data-testid="quick-impersonate-input"
                />
                <Button
                  onClick={quickImpersonateByCode}
                  disabled={searchingCode || !quickImpersonateCode.trim()}
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700 text-white px-3"
                  data-testid="quick-impersonate-btn"
                >
                  {searchingCode ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  ) : (
                    <Shield className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-purple-300/60 mt-2">
                {t('common.description')}
              </p>
            </div>
          )}

          {/* Promo Links - Shop & Rentals at TOP */}
          <div className="mb-4 space-y-2">
            <Link
              to="/webshop"
              className="flex items-center justify-between px-4 py-3 rounded-lg transition-all bg-gradient-to-r from-[#F7931A]/20 to-[#FFD700]/10 border border-[#F7931A]/30 hover:border-[#F7931A] hover:from-[#F7931A]/30 hover:to-[#FFD700]/20 group"
              data-testid="nav-promo-shop"
            >
              <div className="flex items-center gap-3">
                <span className="text-[#F7931A]">
                  <ShoppingBag className="w-5 h-5" />
                </span>
                <span className="font-medium text-[#F7931A]">{t('webshop.title')}</span>
              </div>
              <div className="flex items-center gap-2">
                {activeDiscountCount > 0 && (
                  <span className="px-2 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full animate-pulse">
                    -{maxDiscountPercent}%
                  </span>
                )}
                <Eye className="w-4 h-4 text-[#F7931A] opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </Link>
            
            <Link
              to="/rentals"
              className="flex items-center justify-between px-4 py-3 rounded-lg transition-all bg-gradient-to-r from-[#F7931A]/20 to-[#FFD700]/10 border border-[#F7931A]/30 hover:border-[#F7931A] hover:from-[#F7931A]/30 hover:to-[#FFD700]/20 group"
              data-testid="nav-promo-rentals"
            >
              <div className="flex items-center gap-3">
                <span className="text-[#F7931A]">
                  <Wrench className="w-5 h-5" />
                </span>
                <span className="font-medium text-[#F7931A]">{t('rentals.title')}</span>
              </div>
              <Eye className="w-4 h-4 text-[#F7931A] opacity-0 group-hover:opacity-100 transition-opacity" />
            </Link>
          </div>
          
          <div className="border-t border-white/10 mb-4"></div>

          {/* Navigation */}
          <nav className="space-y-1">
            {activeNavItems.filter(item => !item.external).map((item, index) => {
              // Handle divider
              if (item.type === "divider") {
                return (
                  <div key={`divider-${index}`} className="my-3 border-t border-white/10"></div>
                );
              }

              // Handle collapsible section
              if (item.type === "section") {
                const isCollapsed = collapsedSections[item.id];
                const sectionHasActive = item.children?.some(c => location.pathname === c.href);
                // Auto-expand section if it contains the active route
                const showChildren = !isCollapsed || sectionHasActive;
                const sectionBadgeCount = item.children?.reduce((sum, c) => sum + getBadgeCount(c.label), 0) || 0;

                return (
                  <div key={`section-${item.id}`} className="pt-2">
                    <button
                      type="button"
                      onClick={() => toggleSection(item.id)}
                      className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#64748B] hover:text-[#94A3B8] transition-colors"
                      data-testid={`nav-section-${item.id}`}
                    >
                      <span className="flex items-center gap-2">
                        {item.icon}
                        {item.label}
                        {sectionBadgeCount > 0 && (
                          <span className="ml-1 flex items-center justify-center min-w-[18px] h-4 px-1 text-[10px] font-bold bg-[#FF5F1F] text-white rounded-full normal-case">
                            {sectionBadgeCount}
                          </span>
                        )}
                      </span>
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${showChildren ? '' : '-rotate-90'}`}
                      />
                    </button>
                    {showChildren && (
                      <div className="space-y-1 mt-1">
                        {item.children.map((child) => {
                          const isActive = location.pathname === child.href;
                          const badgeCount = getBadgeCount(child.label);
                          return (
                            <Link
                              key={child.href}
                              to={child.href}
                              className={`flex items-center justify-between pl-6 pr-3 py-2 rounded-lg transition-colors ${
                                isActive
                                  ? "bg-[#23F7DD]/10 text-[#23F7DD] border-l-3 border-[#23F7DD]"
                                  : child.highlight
                                  ? "text-[#F7931A] hover:bg-[#F7931A]/10"
                                  : "text-[#94A3B8] hover:bg-white/5 hover:text-white"
                              }`}
                              data-testid={`nav-${child.label.toLowerCase().replace(/\s/g, '-')}`}
                            >
                              <div className="flex items-center gap-3">
                                {child.icon}
                                <span className="font-medium text-sm">{translateLabel(child.label)}</span>
                              </div>
                              {badgeCount > 0 && (
                                <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold bg-[#FF5F1F] text-white rounded-full animate-pulse">
                                  {badgeCount}
                                </span>
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              // Hide promotion link for team leaders
              if (item.promotionLink && user?.role === 'team_leader') {
                return null;
              }
              
              const isActive = location.pathname === item.href || 
                (item.href.includes('?tab=') && location.pathname + location.search === item.href);
              const badgeCount = getBadgeCount(item.label);
              
              // Special styling for promotion link
              if (item.promotionLink) {
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="flex items-center justify-between px-4 py-3 rounded-lg transition-all bg-gradient-to-r from-purple-500/20 to-purple-600/10 border border-purple-500/30 hover:border-purple-400 hover:from-purple-500/30 group animate-pulse"
                    data-testid="nav-become-tl"
                  >
                    <div className="flex items-center gap-3">
                      <Star className="w-5 h-5 text-purple-400" />
                      <span className="font-bold text-purple-400">{translateLabel(item.label)}</span>
                    </div>
                  </Link>
                );
              }
              
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? "bg-[#23F7DD]/10 text-[#23F7DD] border-l-3 border-[#23F7DD]"
                      : "text-[#94A3B8] hover:bg-white/5 hover:text-white"
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <div className="flex items-center gap-3">
                    {item.icon}
                    <span className="font-medium">{translateLabel(item.label)}</span>
                  </div>
                  {badgeCount > 0 && (
                    <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold bg-[#FF5F1F] text-white rounded-full animate-pulse">
                      {badgeCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Logout Button - Fixed at bottom */}
        <div className="flex-shrink-0 p-6 pt-4 border-t border-white/10 bg-[#0A0A0A]">
          <div className="mb-4">
            <LanguageSwitcher variant="full" className="w-full justify-start" />
          </div>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-[#94A3B8] hover:text-[#FF5F1F] hover:bg-[#FF5F1F]/10"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 mr-3" />
            {t('auth.logout')}
          </Button>
          {/* Version indicator - auto-read from package.json */}
          <div className="mt-4 pt-3 border-t border-white/5 text-center">
            <span className="text-xs text-[#475569]" data-testid="app-version">
              v{process.env.REACT_APP_VERSION}
            </span>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="md:ml-72 min-h-screen pt-16 md:pt-0">
        {/* Desktop Header */}
        <header className="hidden md:flex items-center justify-between px-8 py-6 border-b border-white/10">
          <h1 className="text-2xl font-['Unbounded'] font-bold text-white">{displayTitle}</h1>
          <div className="flex items-center gap-4 relative">
            <LanguageSwitcher />
            <div className="relative" ref={notificationRef}>
              <button 
                onClick={toggleNotifications}
                className="p-2 text-[#94A3B8] hover:text-white rounded-lg hover:bg-white/5 relative"
              >
                <Bell className="w-5 h-5" />
                {unreadNotifications > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-bold animate-pulse">
                    {unreadNotifications > 9 ? '9+' : unreadNotifications}
                  </span>
                )}
              </button>
              
              {/* Notification Dropdown - Desktop */}
              {showNotifications && (
                <div className="absolute top-12 right-0 w-96 max-h-[500px] bg-[#0A0A0A] border border-white/10 rounded-lg shadow-xl overflow-hidden z-50">
                  <div className="p-4 border-b border-white/10 flex items-center justify-between">
                    <span className="font-semibold text-white text-lg">{t('dashboard.notifications')}</span>
                    {unreadNotifications > 0 && (
                      <button 
                        onClick={markAllAsRead}
                        className="text-sm text-[#23F7DD] hover:underline flex items-center gap-1"
                      >
                        <Check className="w-4 h-4" />
                        {t('common.selectAll')}
                      </button>
                    )}
                  </div>
                  <div className="max-h-[400px] overflow-y-auto">
                    {loadingNotifications ? (
                      <div className="p-8 text-center">
                        <div className="w-8 h-8 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin mx-auto"></div>
                      </div>
                    ) : notifications.length === 0 ? (
                      <div className="p-8 text-center text-[#94A3B8]">
                        <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p className="text-lg">{t('dashboard.noNotifications')}</p>
                        <p className="text-sm mt-1">{t('common.noData')}</p>
                      </div>
                    ) : (
                      notifications.slice(0, 15).map((notif) => (
                        <div
                          key={notif.notification_id}
                          onClick={() => handleNotificationClick(notif)}
                          className={`p-4 border-b border-white/5 cursor-pointer hover:bg-white/5 transition-colors ${!notif.read ? 'bg-[#23F7DD]/5 border-l-2 border-l-[#23F7DD]' : ''}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${!notif.read ? 'bg-[#23F7DD]/20' : 'bg-white/5'}`}>
                              {notif.message?.includes('message') || notif.message?.includes('chat') ? (
                                <MessageSquare className={`w-4 h-4 ${!notif.read ? 'text-[#23F7DD]' : 'text-[#94A3B8]'}`} />
                              ) : notif.message?.includes('approval') || notif.message?.includes('approved') ? (
                                <UserCheck className={`w-4 h-4 ${!notif.read ? 'text-[#23F7DD]' : 'text-[#94A3B8]'}`} />
                              ) : (
                                <Bell className={`w-4 h-4 ${!notif.read ? 'text-[#23F7DD]' : 'text-[#94A3B8]'}`} />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm leading-relaxed ${!notif.read ? 'text-white font-medium' : 'text-[#94A3B8]'}`}>
                                {notif.message}
                              </p>
                              <p className="text-xs text-[#475569] mt-1">
                                {new Date(notif.created_at).toLocaleString()}
                              </p>
                            </div>
                            {!notif.read && (
                              <div className="w-2 h-2 rounded-full bg-[#23F7DD] flex-shrink-0 mt-2" />
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6 md:p-8">{children}</div>
      </main>
    </div>
  );
};

// Navigation configs for different roles
// Client navigation - dynamic based on client_type
export const clientNavItems = [
  { href: "/client", label: "Dashboard", icon: <LayoutDashboard className="w-5 h-5" /> },
  { href: "/client/request-project", label: "Publiser Prosjekt", icon: <UserPlus className="w-5 h-5" />, highlight: true },
  { href: "/client/stillastjenester", label: "Stillastjenester", icon: <Building2 className="w-5 h-5" /> },
  { href: "/client/projects-v3", label: "Mine Prosjekter", icon: <FolderKanban className="w-5 h-5" /> },
  { href: "/client/contracts", label: "Kontrakter", icon: <FileText className="w-5 h-5" /> },
  { href: "/client/invoices", label: "Fakturaer", icon: <FileText className="w-5 h-5" /> },
  { type: "divider" },
  { href: "/client/profile", label: "Firmaprofil", icon: <Building2 className="w-5 h-5" /> },
  { type: "divider" },
  { href: "/webshop", label: "Butikk", icon: <ShoppingBag className="w-5 h-5" />, external: true },
  { href: "/rentals", label: "Lei Utstyr", icon: <Wrench className="w-5 h-5" />, external: true },
];

export const workerNavItems = [
  { href: "/worker", labelKey: "nav.dashboard", label: "Dashboard", icon: <LayoutDashboard className="w-5 h-5" /> },
  { href: "/worker/browse-projects", labelKey: "nav.browseProjects", label: "Browse Projects", icon: <Search className="w-5 h-5" /> },
  { href: "/worker/my-projects", labelKey: "nav.myProjects", label: "My Projects", icon: <FolderKanban className="w-5 h-5" />, highlight: true },
  { href: "/worker/agreements", labelKey: "nav.agreements", label: "Agreements", icon: <FileText className="w-5 h-5" /> },
  { href: "/worker/employment-contract", labelKey: "nav.myContract", label: "My Contract", icon: <FileSignature className="w-5 h-5" /> },
  { href: "/worker/earnings", labelKey: "nav.myEarnings", label: "My Earnings", icon: <DollarSign className="w-5 h-5" /> },
  { href: "/worker/timesheets", labelKey: "nav.timesheets", label: "Timesheets", icon: <FileText className="w-5 h-5" /> },
  { href: "/worker/profile", labelKey: "nav.profile", label: "Profile", icon: <User className="w-5 h-5" /> },
  { type: "divider" },
  { href: "/worker/profile?tab=promotion", labelKey: "nav.becomeTeamLeader", label: "⭐ Become Team Leader", icon: <Star className="w-5 h-5" />, highlight: true, promotionLink: true },
  { type: "divider" },
  { href: "/webshop", labelKey: "nav.shop", label: "Shop", icon: <ShoppingBag className="w-5 h-5" />, external: true },
  { href: "/rentals", labelKey: "nav.rentTools", label: "Rent Tools", icon: <Wrench className="w-5 h-5" />, external: true },
];

export const teamLeaderNavItems = [
  { href: "/team-leader", labelKey: "nav.dashboard", label: "Dashboard", icon: <LayoutDashboard className="w-5 h-5" /> },
  { href: "/team-leader/browse-projects", labelKey: "nav.browseProjects", label: "Browse Projects", icon: <Search className="w-5 h-5" />, highlight: true },
  { href: "/team-leader/my-projects", labelKey: "nav.myProjects", label: "My Projects", icon: <FolderKanban className="w-5 h-5" /> },
  { href: "/team-leader/find-workers", labelKey: "nav.findColleague", label: "Find Colleague", icon: <UserPlus className="w-5 h-5" /> },
  { href: "/team-leader/agreements", labelKey: "nav.agreements", label: "Agreements", icon: <FileText className="w-5 h-5" /> },
  { href: "/worker/employment-contract", labelKey: "nav.myContract", label: "My Contract", icon: <FileSignature className="w-5 h-5" /> },
  { href: "/team-leader/teams", labelKey: "nav.myTeams", label: "My Teams", icon: <User className="w-5 h-5" /> },
  { href: "/team-leader/earnings", labelKey: "nav.myEarnings", label: "My Earnings", icon: <DollarSign className="w-5 h-5" /> },
  { href: "/team-leader/profile", labelKey: "nav.profile", label: "Profile", icon: <User className="w-5 h-5" /> },
  { type: "divider" },
  { href: "/webshop", labelKey: "nav.shop", label: "Shop", icon: <ShoppingBag className="w-5 h-5" />, external: true },
  { href: "/rentals", labelKey: "nav.rentEquipment", label: "Rent Equipment", icon: <Wrench className="w-5 h-5" />, external: true },
];

export const adminNavItems = [
  { href: "/admin", label: "Dashboard", icon: <LayoutDashboard className="w-5 h-5" /> },

  { type: "section", id: "projects", label: "Prosjekter", icon: <FolderKanban className="w-4 h-4" />, children: [
    { href: "/local-projects", label: "Local Projects", icon: <Building2 className="w-5 h-5" /> },
    { href: "/projects", label: "Browse Projects", icon: <Search className="w-5 h-5" /> },
    { href: "/admin/project-approval", label: "Project Approval", icon: <UserCheck className="w-5 h-5" /> },
    { href: "/admin/projects", label: "All Projects", icon: <FolderKanban className="w-5 h-5" /> },
    { href: "/admin/project-overview", label: "Project Overview", icon: <BarChart3 className="w-5 h-5" />, highlight: true },
    { href: "/admin/contracts", label: "Project Contracts", icon: <FileText className="w-5 h-5" /> },
    { href: "/admin/underlag", label: "Underlag (Timesheets)", icon: <ClipboardCheck className="w-5 h-5" />, highlight: true },
  ]},

  { type: "section", id: "people", label: "Mennesker", icon: <Users className="w-4 h-4" />, children: [
    { href: "/admin/applications", label: "Applications", icon: <UserCheck className="w-5 h-5" /> },
    { href: "/admin/promotions", label: "Promotions", icon: <User className="w-5 h-5" /> },
    { href: "/admin/workers", label: "Workers", icon: <User className="w-5 h-5" /> },
    { href: "/admin/employment-contracts", label: "Employment Contracts", icon: <FileSignature className="w-5 h-5" /> },
    { href: "/holding/contract-generator", label: "Kontraktgenerator", icon: <FileText className="w-5 h-5" />, highlight: true },
    { href: "/admin/contract-settings", label: "Kontrakt-innstillinger", icon: <FileSignature className="w-5 h-5" />, highlight: true },
    { href: "/admin/roles", label: "Role Management", icon: <UserCheck className="w-5 h-5" /> },
  ]},

  { type: "section", id: "commerce", label: "Handel & Salg", icon: <ShoppingBag className="w-4 h-4" />, children: [
    { href: "/admin/webshop", label: "Webshop", icon: <ShoppingBag className="w-5 h-5" /> },
    { href: "/admin/webshop-appearance", label: "Shop Appearance", icon: <Palette className="w-5 h-5" /> },
    { href: "/admin/marketplace", label: "Marketplace", icon: <Store className="w-5 h-5" />, highlight: true },
    { href: "/admin/payouts", label: "Seller Payouts", icon: <Wallet className="w-5 h-5" /> },
    { href: "/admin/rentals", label: "Rentals", icon: <Wrench className="w-5 h-5" /> },
  ]},

  { type: "section", id: "finance", label: "Økonomi", icon: <DollarSign className="w-4 h-4" />, children: [
    { href: "/admin/earnings", label: "Earnings & Payroll", icon: <DollarSign className="w-5 h-5" /> },
    { href: "/admin/invoices", label: "Invoices", icon: <FileText className="w-5 h-5" /> },
    { href: "/admin/budget-planning", label: "Budget Planning", icon: <DollarSign className="w-5 h-5" /> },
  ]},

  { type: "section", id: "content", label: "Innhold & Marketing", icon: <Palette className="w-4 h-4" />, children: [
    { href: "/admin/landing-page", label: "Landing Page Editor", icon: <Palette className="w-5 h-5" />, highlight: true },
    { href: "/admin/banner", label: "Banner & Bilder", icon: <Megaphone className="w-5 h-5" /> },
    { href: "/admin/testimonials", label: "Anmeldelser", icon: <Star className="w-5 h-5" />, highlight: true },
    { href: "/admin/analytics", label: "Analytics", icon: <BarChart3 className="w-5 h-5" /> },
    { href: "/admin/scroll-analytics", label: "Scroll Tracking", icon: <BarChart3 className="w-5 h-5" /> },
  ]},

  { type: "section", id: "academy", label: "Doserne Academy", icon: <Award className="w-4 h-4" />, children: [
    { href: "/doserne", label: "Academy Home", icon: <LayoutDashboard className="w-5 h-5" />, external: true },
    { href: "/doserne/plan", label: "Daily Plan", icon: <ClipboardCheck className="w-5 h-5" />, external: true, highlight: true },
    { href: "/doserne/prosjekter", label: "My Projects", icon: <FolderKanban className="w-5 h-5" />, external: true },
    { href: "/doserne/kurs", label: "Courses (Kurs)", icon: <FileText className="w-5 h-5" />, external: true },
    { href: "/doserne/kurs/sertifikater", label: "Certificates", icon: <Award className="w-5 h-5" />, external: true },
    { href: "/doserne/progresjon", label: "Progress", icon: <TrendingUp className="w-5 h-5" />, external: true },
    { href: "/doserne/profil", label: "Profile", icon: <User className="w-5 h-5" />, external: true },
    { href: "/doserne/logg-inn", label: "Employee login page", icon: <UserCheck className="w-5 h-5" />, external: true },
  ]},

  { type: "section", id: "system", label: "System", icon: <Settings className="w-4 h-4" />, children: [
    { href: "/admin/holding", label: "Holding Company", icon: <Building2 className="w-5 h-5" /> },
    { href: "/admin/permissions", label: "Access & Permissions", icon: <UserCheck className="w-5 h-5" />, highlight: true, requiresRole: "platform_admin" },
    { href: "/admin/database", label: "Database Viewer", icon: <Database className="w-5 h-5" /> },
    { href: "/admin/roadmap", label: "Platform Roadmap", icon: <FolderKanban className="w-5 h-5" /> },
    { href: "/hvordan-det-fungerer", label: "Platform Guide", icon: <FileText className="w-5 h-5" />, external: true },
  ]},

  { type: "divider" },
  { href: "/webshop", label: "Public Shop", icon: <ShoppingBag className="w-5 h-5" />, external: true },
  { href: "/rentals", label: "Public Rentals", icon: <Wrench className="w-5 h-5" />, external: true },
];

// Client Private navigation (individuals without company)
export const clientPrivateNavItems = [
  { href: "/client", label: "Dashboard", icon: <LayoutDashboard className="w-5 h-5" /> },
  { href: "/client/projects", label: "My Projects", icon: <FolderKanban className="w-5 h-5" /> },
  { href: "/client/contracts", label: "Contracts", icon: <FileText className="w-5 h-5" /> },
  { href: "/client/invoices", label: "Invoices", icon: <FileText className="w-5 h-5" /> },
  { type: "divider" },
  { href: "/webshop", label: "Shop", icon: <ShoppingBag className="w-5 h-5" />, external: true },
  { href: "/rentals", label: "Rent Tools", icon: <Wrench className="w-5 h-5" />, external: true },
];

// Webshop Admin navigation
export const webshopAdminNavItems = [
  { href: "/admin/webshop", label: "Products & Orders", icon: <LayoutDashboard className="w-5 h-5" /> },
  { href: "/admin/webshop-appearance", label: "Appearance", icon: <Palette className="w-5 h-5" />, highlight: true },
  { type: "divider" },
  { href: "/webshop", label: "View Public Shop", icon: <ShoppingBag className="w-5 h-5" />, external: true },
];

// Rentals Admin navigation
export const rentalsAdminNavItems = [
  { href: "/admin/rentals", label: "Equipment & Requests", icon: <LayoutDashboard className="w-5 h-5" /> },
  { type: "divider" },
  { href: "/rentals", label: "View Public Rentals", icon: <Wrench className="w-5 h-5" />, external: true },
];

// Operations Manager navigation
export const operationsManagerNavItems = [
  { href: "/admin", label: "Dashboard", icon: <LayoutDashboard className="w-5 h-5" /> },
  { href: "/projects", label: "Browse Projects", icon: <Search className="w-5 h-5" /> },
  { href: "/admin/project-approval", label: "Project Approval", icon: <UserCheck className="w-5 h-5" /> },
  { href: "/admin/projects", label: "All Projects", icon: <FolderKanban className="w-5 h-5" /> },
  { href: "/admin/applications", label: "Applications", icon: <UserCheck className="w-5 h-5" /> },
  { href: "/admin/workers", label: "Workers", icon: <User className="w-5 h-5" /> },
  { href: "/admin/contracts", label: "Contracts", icon: <FileText className="w-5 h-5" /> },
  { type: "divider" },
  { href: "/webshop", label: "Shop", icon: <ShoppingBag className="w-5 h-5" />, external: true },
  { href: "/rentals", label: "Rent Tools", icon: <Wrench className="w-5 h-5" />, external: true },
];

// Holding Admin navigation
export const holdingAdminNavItems = [
  { href: "/holding", label: "Dashboard", icon: <LayoutDashboard className="w-5 h-5" /> },
  { href: "/holding/projects", label: "Prosjekter", icon: <FolderKanban className="w-5 h-5" /> },
  { href: "/holding/workers", label: "Arbeidere", icon: <Users className="w-5 h-5" /> },
  { href: "/holding/internal-employees", label: "Interne Ansatte", icon: <UserCheck className="w-5 h-5" /> },
  { href: "/holding/contract-generator", label: "Kontraktgenerator", icon: <FileText className="w-5 h-5" /> },
  { href: "/holding/quiz-results", label: "Quiz Resultater", icon: <Award className="w-5 h-5" /> },
  { href: "/holding/stillastjenester", label: "Stillastjenester", icon: <Building2 className="w-5 h-5" /> },
  { href: "/holding/public-quotes", label: "Offentlige Forespørsler", icon: <Globe className="w-5 h-5" /> },
  { href: "/holding/teams", label: "Teams", icon: <User className="w-5 h-5" /> },
  { href: "/holding/finances", label: "Økonomi", icon: <TrendingUp className="w-5 h-5" /> },
  { href: "/holding/contracts", label: "Kontrakter", icon: <FileSignature className="w-5 h-5" /> },
  { type: "divider" },
  { href: "/holding/profile", label: "Firmaprofil", icon: <Building2 className="w-5 h-5" /> },
  { href: "/admin/analytics", label: "📊 Analytics", icon: <BarChart3 className="w-5 h-5" /> },
  { type: "divider" },
  { href: "/admin/roadmap", label: "🗺️ Platform Roadmap", icon: <FolderKanban className="w-5 h-5" /> },
  { href: "/hvordan-det-fungerer", label: "📖 Platform Guide", icon: <FileText className="w-5 h-5" />, external: true },
  { type: "divider" },
  { href: "/webshop", label: "Shop", icon: <ShoppingBag className="w-5 h-5" />, external: true },
  { href: "/rentals", label: "Rent Tools", icon: <Wrench className="w-5 h-5" />, external: true },
];


export default DashboardLayout;